/**
 * The Roles screen (WALLIYO-0131), as a band, a rail and a panel.
 *
 * It was a table: a name, a sentence and a number, with everything that
 * mattered crammed into one cell. Four people open this screen and it served
 * none of them — the Owner asking who can do the dangerous things, somebody
 * comparing two roles, somebody choosing one for a new joiner, and somebody
 * wondering whether a role is dead.
 *
 * So the band answers the first question before anything is read, the rail is
 * ordered by how far each role reaches rather than alphabetically, and the
 * panel is where a role is actually managed — because managing one used to
 * mean three other screens.
 */

import type { StaffFeatureId } from "@wallyo/contracts/actions";
import type { StaffMemberListItem, StaffRoleListItem } from "@wallyo/contracts/console";

import { categoryName, featureName, permissionName, t, type MessageId } from "../../i18n";
import { screenHref } from "../../navigation/screens";
import { hrHref } from "../hr/hr-route";
import { permissionsHref } from "../permissions/PermissionsScreen";
import { byCategory, compare, reachOf, summarise, type Reach, type RolesOverview } from "./roles-summary";

const REACH_LABEL: Record<Reach, MessageId> = {
  critical: "roles.reach.critical",
  high: "roles.reach.high",
  low: "roles.reach.low",
};

/**
 * What is worth saying about the roles as a whole, if anything.
 *
 * Four figures in a band was a dashboard for a screen with five rows: with one
 * role it read "1 · 1 · 1 · 0", four numbers to say there is one role and
 * somebody has it. So it is a sentence, and only when there is a sentence to
 * say — a console with three sensible roles and nothing wrong should say
 * nothing at all and let the list speak.
 */
export function RolesNotice({ overview, holdersKnown }: { overview: RolesOverview; holdersKnown: boolean }) {
  const said: React.ReactNode[] = [];

  if (overview.critical > 0) {
    said.push(
      <span key="critical" className="danger">
        {holdersKnown
          ? t("roles.notice.critical", {
              roles: String(overview.critical),
              people: String(overview.peopleWithCritical),
            })
          : t("roles.notice.criticalOnly", { roles: String(overview.critical) })}
      </span>,
    );
  }
  // A role nobody holds is either new or dead, and only worth raising once
  // there is more than the one somebody just made.
  if (overview.unheld > 0) {
    said.push(
      <span key="unheld" className="warn-text">
        {t("roles.notice.unheld", { count: String(overview.unheld) })}
      </span>,
    );
  }

  if (said.length === 0) return null;
  return (
    <p className="roles-notice" role="note">
      {said}
    </p>
  );
}

export function RoleRail({
  roles,
  selectedId,
  onSelect,
}: {
  roles: readonly StaffRoleListItem[];
  selectedId: string | null;
  onSelect: (staffRoleId: string) => void;
}) {
  return (
    <nav className="card role-rail" aria-label={t("roles.title")}>
      {roles.map((role) => {
        const summary = summarise(role);
        const reach = reachOf(summary);
        const on = role.staffRoleId === selectedId;
        return (
          <button
            key={role.staffRoleId}
            type="button"
            className={on ? "role-rail-item on" : "role-rail-item"}
            aria-current={on ? "true" : undefined}
            onClick={() => onSelect(role.staffRoleId)}
          >
            <span className={`reach ${reach}`} aria-hidden="true" />
            <span className="role-rail-name">
              <span className="role-rail-title">
                <strong>{role.name}</strong>
                {/* Either new, or dead. Both are worth knowing, and a count of
                    zero in a column nobody scans says neither. */}
                {role.memberCount === 0 ? <em className="unheld">{t("roles.rail.unheld")}</em> : null}
              </span>
              <span className="foot">
                {t("roles.rail.people", { count: String(role.memberCount) })}
                {" · "}
                {summary.everything
                  ? t("roles.covers.everything")
                  : t("roles.covers.features", { count: String(summary.features) })}
                {summary.critical > 0 ? (
                  <>
                    {" · "}
                    <span className="danger">{t("roles.covers.critical", { count: String(summary.critical) })}</span>
                  </>
                ) : null}
              </span>
            </span>
          </button>
        );
      })}
    </nav>
  );
}

function Block({
  title,
  help,
  action,
  children,
}: {
  title: string;
  help?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="role-block">
      <header>
        <h3>{title}</h3>
        {help ? <span className="foot">{help}</span> : null}
        <div className="grow" />
        {action}
      </header>
      {children}
    </section>
  );
}

export function RolePanel(props: {
  role: StaffRoleListItem;
  /** Everybody who holds it, for the block that manages that. */
  holders: readonly StaffMemberListItem[];
  /** False when the staff list could not be read: say so, never "nobody". */
  holdersKnown: boolean;
  /** The other roles, for the comparison. */
  others: readonly StaffRoleListItem[];
  comparedWith: StaffRoleListItem | null;
  editable: boolean;
  /** The screens it opens, from the same function the menu asks. */
  opens: readonly string[];
  onEdit: () => void;
  onGive: () => void;
  onTake: (member: StaffMemberListItem) => void;
  onCompare: (staffRoleId: string) => void;
}) {
  const { role, holders } = props;
  const summary = summarise(role);
  const reach = reachOf(summary);
  const critical = role.features.filter((grant) => grant.sensitivity === "critical");
  const biggest = summary.areas[0]?.count ?? 1;
  const difference = props.comparedWith ? compare(role, props.comparedWith) : null;

  return (
    <section className="card role-panel">
      <header className="role-panel-head">
        <div>
          <h2>{role.name}</h2>
          <p className="foot">{role.description ?? t("roles.noDescription")}</p>
        </div>
        <div className="grow" />
        <span className={`pill reach-pill ${reach}`}>{t(REACH_LABEL[reach])}</span>
        {props.editable ? (
          <button type="button" className="btn quiet small" onClick={props.onEdit}>
            {t("roles.edit")}
          </button>
        ) : null}
      </header>

      <div className="role-figures">
        <div>
          <span className="figure">{role.memberCount}</span>
          <span className="foot">{t("roles.figure.people")}</span>
        </div>
        <div>
          <span className="figure">{summary.everything ? t("roles.covers.everything") : summary.features}</span>
          <span className="foot">{t("roles.figure.features")}</span>
        </div>
        <div>
          <span className="figure">{summary.permissions}</span>
          <span className="foot">{t("roles.figure.permissions")}</span>
        </div>
        <div className={summary.critical > 0 ? "danger" : undefined}>
          <span className="figure">{summary.critical}</span>
          <span className="foot">{t("roles.figure.critical")}</span>
        </div>
      </div>

      <Block
        title={t("roles.block.holders")}
        help={t("roles.block.holdersHelp")}
        action={
          props.editable && props.holdersKnown ? (
            <button type="button" className="btn quiet small" onClick={props.onGive}>
              {t("roles.block.give")}
            </button>
          ) : null
        }
      >
        {!props.holdersKnown ? (
          <p className="foot">{t("roles.holdersUnknown")}</p>
        ) : holders.length === 0 ? (
          <p className="foot">{t("roles.block.holdersNone")}</p>
        ) : (
          <ul className="hr-plain">
            {holders.map((member) => (
              <li key={member.staffMemberId} className="role-holder">
                <a href={hrHref({ uuid: member.uuid })}>{member.name}</a>
                <span className="mono foot">{member.uuid}</span>
                <span className="foot">
                  {member.roles
                    .filter((each) => each.staffRoleId !== role.staffRoleId)
                    .map((each) => each.name)
                    .join(", ")}
                </span>
                <div className="grow" />
                {props.editable ? (
                  <button type="button" className="btn quiet small" onClick={() => props.onTake(member)}>
                    {t("roles.block.take")}
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </Block>

      <Block
        title={t("roles.block.grants")}
        help={t("roles.block.grantsHelp")}
        action={
          <a className="btn quiet small" href={permissionsHref(role.staffRoleId)}>
            {t("roles.block.change")}
          </a>
        }
      >
        {/* The critical ones in words, first. That is the sentence somebody
            signs off on, and a chip buried among forty-seven never said it. */}
        {critical.length > 0 ? (
          <div className="role-critical">
            <div className="chips">
              {critical.map((grant) => (
                <span className="pill bad" key={grant.settingId}>
                  {featureName(grant.settingId)}
                </span>
              ))}
            </div>
            <p className="foot">{t("roles.block.criticalWarning")}</p>
          </div>
        ) : null}

        <div className="role-bars">
          {summary.areas.map((area) => (
            <div className="role-bar" key={area.category}>
              <span className="role-bar-name">{categoryName(area.category)}</span>
              {/* Relative to the biggest area, not to the catalogue: the
                  question is the shape of this role, not its share of all. */}
              <span className="role-bar-track">
                <span className="role-bar-fill" style={{ width: `${Math.round((area.count / biggest) * 100)}%` }} />
              </span>
              <span className="role-bar-count mono">{area.count}</span>
            </div>
          ))}
        </div>

        <details className="role-all">
          <summary>{t("roles.covers.seeAll", { count: String(summary.features + summary.permissions) })}</summary>
          <div className="role-all-body">
            {byCategory(role).map((group) => (
              <div key={group.category}>
                <h4>{categoryName(group.category)}</h4>
                <div className="chips">
                  {group.settingIds.map((settingId) => (
                    <span key={settingId} className="pill">
                      {featureName(settingId as StaffFeatureId)}
                    </span>
                  ))}
                </div>
              </div>
            ))}
            {role.permissions.length > 0 ? (
              <div>
                <h4>{t("roles.covers.permissionsHeading")}</h4>
                <div className="chips">
                  {role.permissions.map((grant) => (
                    <span key={grant.settingId} className="pill info">
                      {permissionName(grant.settingId)}
                    </span>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        </details>
      </Block>

      <Block title={t("roles.block.opens")} help={t("roles.block.opensHelp")}>
        {props.opens.length === 0 ? (
          <p className="foot">{t("roles.block.opensNone")}</p>
        ) : (
          <div className="chips">
            {props.opens.map((label) => (
              <span className="pill" key={label}>
                {label}
              </span>
            ))}
          </div>
        )}
      </Block>

      {props.others.length > 0 ? (
        <Block
          title={t("roles.block.compare")}
          action={
            <label className="hr-chip">
              <span className="foot">{t("roles.block.compareWith")}</span>
              <select
                value={props.comparedWith?.staffRoleId ?? ""}
                onChange={(event) => props.onCompare(event.currentTarget.value)}
              >
                <option value="">—</option>
                {props.others.map((other) => (
                  <option key={other.staffRoleId} value={other.staffRoleId}>
                    {other.name}
                  </option>
                ))}
              </select>
            </label>
          }
        >
          {difference ? (
            <>
              <div className="role-diff">
                <span className="ok">{t("roles.compare.only", { count: String(difference.onlyLeft.length) })}</span>
                <span className="danger">
                  {t("roles.compare.theirs", { count: String(difference.onlyRight.length) })}
                </span>
                <span className="foot">{t("roles.compare.shared", { count: String(difference.shared.length) })}</span>
              </div>
              {/* Which ones, not how many. Three numbers say the roles differ;
                  choosing between them needs to know where. */}
              <div className="role-diff-lists">
                {difference.onlyLeft.length > 0 ? (
                  <div>
                    <h4 className="ok">{role.name}</h4>
                    <div className="chips">
                      {difference.onlyLeft.map((settingId) => (
                        <span className="pill ok" key={settingId}>
                          {featureName(settingId as StaffFeatureId)}
                        </span>
                      ))}
                    </div>
                  </div>
                ) : null}
                {difference.onlyRight.length > 0 ? (
                  <div>
                    <h4 className="danger">{props.comparedWith?.name}</h4>
                    <div className="chips">
                      {difference.onlyRight.map((settingId) => (
                        <span className="pill bad" key={settingId}>
                          {featureName(settingId as StaffFeatureId)}
                        </span>
                      ))}
                    </div>
                  </div>
                ) : null}
                {difference.onlyLeft.length === 0 && difference.onlyRight.length === 0 ? (
                  <p className="foot">{t("roles.compare.same")}</p>
                ) : null}
              </div>
            </>
          ) : null}
        </Block>
      ) : null}

      <footer className="role-panel-foot">
        <a className="btn quiet small" href={`${screenHref("hr")}/employees`}>
          {t("roles.grants.features")}
        </a>
      </footer>
    </section>
  );
}
